import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Search, ArrowRight, User, Briefcase, Stethoscope, Home as HomeIcon, 
  Truck, HeartHandshake, Microscope, Dna, ShieldCheck, 
  CheckCircle, Activity, Droplets, Heart, 
  AlertCircle, Zap, Star, Users, Quote, Mail, Share2, Loader2, ShoppingCart,
  Calendar, Smartphone, ChevronLeft, ChevronRight
} from 'lucide-react';
import { homeAPI } from '../../services/api';
import api from '../../api/api';
import { useCart } from '../../context/CartContext';
import TestDetailContent from '../TestCatalog/TestDetailContent';
import ShareModal from '../../components/ShareModal/ShareModal';
import { resolveCatalogTest } from '../../utils/testResolver';
import { useOfferStream } from '../../hooks/useOfferStream';
import { motion } from 'framer-motion';
import './Home.css';
import './DynamicOffers.css';

// Decreasing Live Countdown Timer widget supporting Start Date, End Date, and Unlimited mode
const CountdownTimer = ({ startDate, endDate, timeLeft, isUnlimited }) => {
  const targetEndDate = endDate || (timeLeft && Date.parse(timeLeft) ? timeLeft : null);

  const [timerState, setTimerState] = useState({
    mode: 'unlimited',
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    if (isUnlimited || (!targetEndDate && !startDate)) {
      setTimerState({ mode: 'unlimited', days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const tick = () => {
      const now = Date.now();
      const startMs = startDate ? Date.parse(startDate) : 0;
      const endMs = targetEndDate ? Date.parse(targetEndDate) : 0;

      // 1. Upcoming Check
      if (startMs && startMs > now) {
        const diff = startMs - now;
        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff % 86400000) / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimerState({ mode: 'upcoming', days, hours, minutes, seconds });
        return;
      }

      // 2. Expiry Check
      if (endMs && endMs <= now) {
        setTimerState({ mode: 'expired', days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      // 3. Live Countdown to Expiry
      if (endMs && endMs > now) {
        const diff = endMs - now;
        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff % 86400000) / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimerState({ mode: 'live', days, hours, minutes, seconds });
        return;
      }

      setTimerState({ mode: 'unlimited', days: 0, hours: 0, minutes: 0, seconds: 0 });
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startDate, targetEndDate, isUnlimited]);

  if (timerState.mode === 'unlimited') {
    return (
      <span className="offer-unlimited-pill">
        ♾️ Ongoing Deal
      </span>
    );
  }

  if (timerState.mode === 'expired') {
    return (
      <span className="offer-expired-pill">
        ⏰ Expired
      </span>
    );
  }

  const isUpcoming = timerState.mode === 'upcoming';

  return (
    <div className="countdown-timer-wrap">
      <div className="countdown-timer-boxes">
        <div className="timer-box">
          <span className="timer-num">{String(timerState.days).padStart(2, '0')}</span>
          <span className="timer-sub">Day</span>
        </div>
        <span className="timer-colon">:</span>
        <div className="timer-box">
          <span className="timer-num">{String(timerState.hours).padStart(2, '0')}</span>
          <span className="timer-sub">Hr</span>
        </div>
        <span className="timer-colon">:</span>
        <div className="timer-box">
          <span className="timer-num">{String(timerState.minutes).padStart(2, '0')}</span>
          <span className="timer-sub">Min</span>
        </div>
        <span className="timer-colon">:</span>
        <div className="timer-box">
          <span className="timer-num" style={{ color: isUpcoming ? '#f59e0b' : '#38bdf8' }}>{String(timerState.seconds).padStart(2, '0')}</span>
          <span className="timer-sub">Sec</span>
        </div>
      </div>
    </div>
  );
};

const TestimonialAvatar = ({ photoUrl, name, isGoogle, isVerified, cleanInitials }) => {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [photoUrl]);

  const effectiveUrl = useMemo(() => {
    if (!photoUrl) return '';
    if (photoUrl.includes('googleusercontent.com') || photoUrl.includes('ggpht.com')) {
      return `/api/home/avatar-proxy/?url=${encodeURIComponent(photoUrl)}&name=${encodeURIComponent(name || '')}`;
    }
    return photoUrl;
  }, [photoUrl, name]);

  const initial = (name || cleanInitials || 'M').trim().charAt(0).toUpperCase();
  const colors = ['#2563eb', '#059669', '#7c3aed', '#db2777', '#d97706', '#0891b2'];
  const colorIndex = (name ? name.charCodeAt(0) : 0) % colors.length;
  const bgColor = colors[colorIndex];

  if (effectiveUrl && !imgFailed) {
    return (
      <img 
        src={effectiveUrl} 
        alt={name}
        referrerPolicy="no-referrer"
        loading="lazy"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={() => setImgFailed(true)}
      />
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: bgColor,
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: '1.05rem',
        textTransform: 'uppercase'
      }}
    >
      {initial}
    </div>
  );
};

const ALL_OFFER_SKELETON_SLOTS = [
  {
    slotKey: 'seasonal',
    type: 'Seasonal Special',
    className: 'seasonal',
    gradient: 'linear-gradient(135deg, #065f46 0%, #10b981 50%, #34d399 100%)',
    badgeWidth: '145px',
    timerWidth: '105px'
  },
  {
    slotKey: 'weekly',
    type: 'Weekly Offer',
    className: 'weekly',
    gradient: 'linear-gradient(135deg, #1e3a5f 0%, #3b82f6 50%, #60a5fa 100%)',
    badgeWidth: '135px',
    timerWidth: '105px'
  },
  {
    slotKey: 'monthly',
    type: 'Monthly Bundle',
    className: 'monthly',
    gradient: 'linear-gradient(135deg, #7f1d1d 0%, #e11d48 50%, #fb7185 100%)',
    badgeWidth: '150px',
    timerWidth: '120px'
  },
  {
    slotKey: 'phlebotomy',
    type: 'Phlebotomy Offer',
    className: 'phlebotomy-slot',
    gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 50%, #8b5cf6 100%)',
    badgeWidth: '155px',
    timerWidth: '105px'
  }
];

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems, removeFromCart, triggerAddToCartAnimation, addToCart } = useCart();
  const [appPromoConfig, setAppPromoConfig] = useState({
    play_store_url: 'https://play.google.com/store/apps/details?id=com.musb.diagnostics&hl=en_IN',
    app_store_url: 'https://apps.apple.com/in/app/musb-diagnostics/id6797962528'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [heroData, setHeroData] = useState(null);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [communityStats, setCommunityStats] = useState(null);
  const [activeReviewIdx, setActiveReviewIdx] = useState(0);
  const [offersData, setOffersData] = useState([]);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState({ loading: false, message: '', error: false });
  const [loading, setLoading] = useState(true);
  const [flippedIdx, setFlippedIdx] = useState(null);
  const [seasonalIdx, setSeasonalIdx] = useState(0);
  const [monthlyIdx, setMonthlyIdx] = useState(0);
  const [weeklyIdx, setWeeklyIdx] = useState(0);
  const [phlebIdx, setPhlebIdx] = useState(0);

  // Catalog tests for Hero Offer card test detail viewer
  const [catalogTests, setCatalogTests] = useState([]);
  const [selectedTestModal, setSelectedTestModal] = useState(null);
  const [shareModalData, setShareModalData] = useState({ isOpen: false, test: null });

  const openTestModal = useCallback((target) => {
    if (!target) return;
    const testObj = typeof target === 'object' ? target : (catalogTests || []).find(t => String(t.id || t._id || t.code) === String(target));
    if (testObj) {
      setSelectedTestModal(testObj);
    }
    const testParam = typeof target === 'object' ? (target.slug || target.id || target._id || target.code) : target;
    const params = new URLSearchParams(location.search);
    if (params.get('test') !== String(testParam) && params.get('detailTest') !== String(testParam)) {
      params.set('test', String(testParam));
      navigate({ search: params.toString() }, { replace: false });
    }
  }, [catalogTests, location.search, navigate]);

  const closeTestModal = useCallback(() => {
    setSelectedTestModal(null);
    const params = new URLSearchParams(window.location.search);
    if (params.has('test') || params.has('detailTest') || params.has('panel')) {
      params.delete('test');
      params.delete('detailTest');
      params.delete('panel');
      const newSearch = params.toString();
      navigate(newSearch ? `?${newSearch}` : window.location.pathname, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const detailId = params.get('test') || params.get('detailTest') || params.get('panel');
    if (!detailId) {
      setSelectedTestModal(null);
      return;
    }
    // Check already-loaded catalogTests first (populated on repeat opens)
    const detailIdLower = String(detailId).trim().toLowerCase();
    const found = (catalogTests || []).find(t => {
      if (!t) return false;
      const idMatch = String(t.id || t._id || t.code || '').toLowerCase() === detailIdLower;
      const slugMatch = String(t.slug || '').toLowerCase() === detailIdLower;
      const titleSlug = String(t.title || t.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      return idMatch || slugMatch || titleSlug === detailIdLower;
    });
    if (found) {
      setSelectedTestModal(found);
      return;
    }
    // ponytail: fetch single test by ID — avoids dumping the full catalog just
    // for a deep-link. Falls back to bulk fetch only if the single-item endpoint
    // returns nothing (e.g. slug-only lookup).
    api.get(`/api/catalog/tests/${detailId}/`).then(res => {
      if (res.data && res.ok) {
        setSelectedTestModal(res.data);
      } else {
        // Slug/title lookup: load full catalog once and cache in state
        api.get('/api/catalog/tests/').then(bulk => {
          if (!bulk.ok) return;
          const all = Array.isArray(bulk.data) ? bulk.data : (bulk.data?.results || []);
          setCatalogTests(all);
          const match = all.find(t => {
            const titleSlug = String(t.title || t.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
            return String(t.slug || '').toLowerCase() === detailIdLower || titleSlug === detailIdLower;
          });
          if (match) setSelectedTestModal(match);
        }).catch(() => {});
      }
    }).catch(() => {});
  }, [location.search, catalogTests]);

  const fetchData = React.useCallback(async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      // ponytail: catalog/tests omitted — full dump (slowest call) is only needed
      // for ?test= deep-links; fetched lazily in the URL-param effect below.
      const [heroRes, servicesRes, testimonialsRes, statsRes, offersRes, promoRes] = await Promise.allSettled([
        homeAPI.getHero(),
        homeAPI.getServices(),
        homeAPI.getTestimonials(),
        homeAPI.getCommunityStats(),
        homeAPI.getOffers(),
        api.get('/api/home/app-promo/config/')
      ]);

      if (heroRes.status === 'fulfilled' && heroRes.value.ok) setHeroData(heroRes.value.data[0]);
      if (servicesRes.status === 'fulfilled' && servicesRes.value.ok) setServices(servicesRes.value.data);
      if (testimonialsRes.status === 'fulfilled' && testimonialsRes.value.ok) {
        const rawList = Array.isArray(testimonialsRes.value.data) ? testimonialsRes.value.data : [];
        const sortedList = [...rawList].sort((a, b) => {
          if (Boolean(a.is_featured_home) !== Boolean(b.is_featured_home)) return a.is_featured_home ? -1 : 1;
          const tA = new Date(a.created_at || a.date || 0).getTime() || 0;
          const tB = new Date(b.created_at || b.date || 0).getTime() || 0;
          return tB - tA;
        });
        setTestimonials(sortedList);
      }
      if (statsRes.status === 'fulfilled' && statsRes.value?.ok) setCommunityStats(statsRes.value.data);
      if (promoRes.status === 'fulfilled' && promoRes.value?.data) {
        setAppPromoConfig(prev => ({
          ...prev,
          play_store_url: promoRes.value.data.play_store_url || prev.play_store_url,
          app_store_url: promoRes.value.data.app_store_url || prev.app_store_url
        }));
      }
      if (offersRes.status === 'fulfilled' && offersRes.value.ok) {
        const rawOffers = Array.isArray(offersRes.value.data) ? offersRes.value.data : [];
        const seen = new Set();
        const uniqueOffers = rawOffers.filter(o => {
          const k = `${(o.title || '').trim().toLowerCase()}_${(o.offer_type || '').trim().toLowerCase()}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });

        const sortPriority = { seasonal: 1, weekly: 2, monthly: 3, phlebotomy: 4 };
        const getSortWeight = (o) => {
          if (o.offer_category === 'phlebotomy') return 4;
          const t = (o.offer_type || '').toLowerCase();
          return sortPriority[t] || 5;
        };
        uniqueOffers.sort((a, b) => getSortWeight(a) - getSortWeight(b));
        setOffersData(uniqueOffers);
      }
    } catch (error) {
      console.error("Error fetching home data:", error);
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Real-time listener: uses SSE (Server-Sent Events) to sync offer updates (pause, relaunch, edit, delete)
  // dynamically across all devices, tabs, and windows in real-time.
  useOfferStream(React.useCallback(() => {
    fetchData(false);
  }, [fetchData]), null, { sse: true });

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/tests?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus({ loading: true, message: '', error: false });
    const res = await homeAPI.subscribeNewsletter(newsletterEmail);
    if (res.ok) {
      setNewsletterStatus({ loading: false, message: res.data.message, error: false });
      setNewsletterEmail('');
    } else {
      setNewsletterStatus({ loading: false, message: 'Subscription failed. Please try again.', error: true });
    }
  };

  const getIcon = (iconName) => {
    const icons = { User, Briefcase, Stethoscope, Home: HomeIcon, Truck, HeartHandshake, Microscope, Dna, Activity, Droplets, AlertCircle, Heart, Zap };
    const IconComp = icons[iconName] || Activity;
    return <IconComp />;
  };

  // Group offers by type for the slideshow
  const seasonalOffers = useMemo(() => offersData.filter(o => o.offer_type?.toLowerCase() === 'seasonal'), [offersData]);
  const monthlyOffers = useMemo(() => offersData.filter(o => o.offer_type?.toLowerCase() === 'monthly'), [offersData]);
  const weeklyOffers = useMemo(() => offersData.filter(o => o.offer_type?.toLowerCase() === 'weekly'), [offersData]);
  const phlebotomyOffers = useMemo(() => offersData.filter(o => o.offer_category === 'phlebotomy'), [offersData]);

  const showSeasonal = seasonalOffers.length > 0;
  const showMonthly = monthlyOffers.length > 0;
  const showWeekly = weeklyOffers.length > 0;
  const showPhleb = phlebotomyOffers.length > 0;

  const gridClassName = useMemo(() => {
    const active = [];
    if (showSeasonal) active.push('seasonal');
    if (showWeekly) active.push('weekly');
    if (showMonthly) active.push('monthly');
    if (showPhleb) active.push('phlebotomy');

    return `has-${active.length}-cards ${showSeasonal ? 'has-seasonal' : ''}`;
  }, [showSeasonal, showWeekly, showMonthly, showPhleb]);

  // Track active offer slots to display the exact number of skeleton cards (1, 2, 3, or 4) during loading
  const [activeSlotKeys, setActiveSlotKeys] = useState(() => {
    try {
      const saved = localStorage.getItem('musb_home_offers_slots');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {
      // fallback to 4 default active slots
    }
    return ['seasonal', 'weekly', 'monthly', 'phlebotomy'];
  });

  useEffect(() => {
    if (offersData.length > 0) {
      const active = [];
      if (showSeasonal) active.push('seasonal');
      if (showWeekly) active.push('weekly');
      if (showMonthly) active.push('monthly');
      if (showPhleb) active.push('phlebotomy');

      const finalKeys = active.length > 0 ? active : (offersData.length > 0 ? ['seasonal', 'weekly', 'monthly', 'phlebotomy'].slice(0, Math.min(offersData.length, 4)) : ['seasonal', 'weekly', 'monthly', 'phlebotomy']);
      setActiveSlotKeys(finalKeys);
      try {
        localStorage.setItem('musb_home_offers_slots', JSON.stringify(finalKeys));
      } catch {}
    }
  }, [offersData, showSeasonal, showWeekly, showMonthly, showPhleb]);

  const skeletonSlots = useMemo(() => {
    const matched = ALL_OFFER_SKELETON_SLOTS.filter(s => activeSlotKeys.includes(s.slotKey));
    return matched.length > 0 ? matched : ALL_OFFER_SKELETON_SLOTS;
  }, [activeSlotKeys]);

  // Auto-rotate slideshows
  useEffect(() => {
    if (seasonalOffers.length <= 1) return;
    const timer = setInterval(() => {
      setSeasonalIdx(prev => (prev + 1) % seasonalOffers.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [seasonalOffers.length]);

  useEffect(() => {
    if (monthlyOffers.length <= 1) return;
    const timer = setInterval(() => {
      setMonthlyIdx(prev => (prev + 1) % monthlyOffers.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [monthlyOffers.length]);

  useEffect(() => {
    if (weeklyOffers.length <= 1) return;
    const timer = setInterval(() => {
      setWeeklyIdx(prev => (prev + 1) % weeklyOffers.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [weeklyOffers.length]);

  useEffect(() => {
    if (phlebotomyOffers.length <= 1) return;
    const timer = setInterval(() => {
      setPhlebIdx(prev => (prev + 1) % phlebotomyOffers.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [phlebotomyOffers.length]);

  // Auto-rotate testimonials / community reviews ("came and go" smooth rotating effect)
  useEffect(() => {
    if (!testimonials || testimonials.length <= 1) return;
    const timer = setInterval(() => {
      setActiveReviewIdx(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials]);

  // Real-time testimonials sync via Server-Sent Events (SSE)
  useEffect(() => {
    let es;
    try {
      es = new EventSource('/api/home/testimonials/stream/');
      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data && Array.isArray(data.reviews) && data.reviews.length > 0) {
            const sortedList = [...data.reviews].sort((a, b) => {
              if (Boolean(a.is_featured_home) !== Boolean(b.is_featured_home)) return a.is_featured_home ? -1 : 1;
              const tA = new Date(a.created_at || a.date || 0).getTime() || 0;
              const tB = new Date(b.created_at || b.date || 0).getTime() || 0;
              return tB - tA;
            });
            setTestimonials(sortedList);
          }
        } catch (_) {}
      };
      es.onerror = () => {
        // EventSource will auto-reconnect
      };
    } catch (_) {}

    return () => {
      if (es) {
        es.close();
      }
    };
  }, []);

  const SLIDE_CONFIG = {
    Seasonal: {
      gradient: 'linear-gradient(135deg, #065f46 0%, #10b981 50%, #34d399 100%)',
      icon: <ShieldCheck size={18} />,
      subtitle: 'Specialized panels designed for current health needs.',
      cta: 'Explore Seasonal Offers',
      emoji: '🌿',
    },
    Monthly: {
      gradient: 'linear-gradient(135deg, #7f1d1d 0%, #e11d48 50%, #fb7185 100%)',
      icon: <CheckCircle size={18} />,
      subtitle: 'Best value for routine checkups.',
      cta: 'View Bundle',
      emoji: '🔥',
    },
    Weekly: {
      gradient: 'linear-gradient(135deg, #1e3a5f 0%, #3b82f6 50%, #60a5fa 100%)',
      icon: <Zap size={18} fill="currentColor" />,
      subtitle: 'Up to 30% off top diagnostic tests.',
      cta: 'Claim Deal',
      emoji: '⚡',
    },
    Phlebotomy: {
      gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 50%, #8b5cf6 100%)',
      icon: <Truck size={18} />,
      subtitle: 'Certified specialist doorstep blood draw service.',
      cta: 'Book Mobile Draw',
      emoji: '🚐',
    },
  };

  // Render a single slideshow card
  const renderSlideCard = (offersList, currentIdx, setIdx, type, className) => {
    const config = SLIDE_CONFIG[type] || SLIDE_CONFIG.Weekly;
    const offer = offersList[currentIdx];

    const isFeatured = type === 'Seasonal' || type === 'Weekly';
    const isPhleb = type === 'Phlebotomy' || offer?.offer_category === 'phlebotomy';

    // Check if the offer is percentage-based (e.g. discount_value has % or title has % or is_percentage flag)
    const discStr = String(offer?.discount_value || '');
    const titleStr = String(offer?.title || '');
    const pctMatch = discStr.match(/(\d+)\s*%/) || titleStr.match(/(\d+)\s*%/);
    
    // When entering 20% (or any percentage deal), or for phlebotomy offers with percentage:
    // Completely remove $80 or any strike price from percentage deals
    const isPercentageDeal = Boolean(pctMatch) || offer?.is_percentage === true || isPhleb;
    const pctDisplay = pctMatch ? `${pctMatch[1]}% OFF` : (offer?.discount_value || '20% OFF');

    // Uniform robust price parsing across all categories
    const rawOrig = offer?.original_price !== undefined && offer?.original_price !== ''
      ? parseFloat(offer.original_price) 
      : (offer?.market_price ? parseFloat(offer.market_price) : (isPhleb ? null : 127));
    const origPrice = rawOrig !== null && !isNaN(rawOrig) ? rawOrig : 0;

    let discPrice = offer?.discounted_price !== undefined && offer?.discounted_price !== ''
      ? parseFloat(offer.discounted_price) 
      : undefined;

    if ((discPrice === undefined || isNaN(discPrice)) && origPrice > 0) {
      if (pctMatch) {
        discPrice = origPrice * (1 - parseFloat(pctMatch[1]) / 100);
      } else {
        discPrice = origPrice * 0.8;
      }
    }

    const saveAmt = origPrice > 0 && discPrice && (origPrice - discPrice) > 0 
      ? (origPrice - discPrice).toFixed(0) 
      : '0';

    return (
      <div 
        key={`hero-slot-${type}`}
        className={`offer-card-new ${className} ${isFeatured ? 'featured-hero-card' : ''}`}
        style={{ cursor: 'pointer' }}
        onClick={() => {
          if (isPhleb) {
            navigate('/book?category=phlebotomy');
          } else {
            const rawIncludes = Array.isArray(offer?.includes) && offer.includes.length > 0 ? offer.includes : [];
            let matched = null;
            for (const inc of rawIncludes) {
              matched = resolveCatalogTest(inc, catalogTests);
              if (matched) break;
            }
            if (!matched && offer?.title) {
              matched = resolveCatalogTest(offer.title, catalogTests);
            }
            if (matched) {
              setSelectedTestModal(matched);
            } else {
              navigate(`/offers?type=${type.toLowerCase()}`);
            }
          }
        }}
      >
        {/* Gradient background instead of images */}
        <div className="offer-card-bg" style={{ background: config.gradient }}>
          <div className="offer-card-bg-pattern"></div>
          {/* Decorative shapes */}
          <div className="offer-deco offer-deco-1"></div>
          <div className="offer-deco offer-deco-2"></div>
          <div className="offer-deco offer-deco-3"></div>
        </div>

        <div className="offer-overlay">
          <div className="offer-overlay-top">
            <span className="offer-badge-new">
              <motion.span
                style={{ display: 'inline-block', marginRight: '4px' }}
                animate={{ scale: [1, 1.25, 1], rotate: [0, -10, 10, 0] }}
                transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
              >
                {config.emoji}
              </motion.span>
              <span>{type} {type === 'Monthly' ? 'Bundle' : type === 'Seasonal' ? 'Special' : 'Offer'}</span>
            </span>
            <div className="offer-overlay-top-right">
              <CountdownTimer 
                startDate={offer?.start_date} 
                endDate={offer?.end_date} 
                timeLeft={offer?.time_left} 
                isUnlimited={offer?.is_unlimited} 
              />
            </div>
          </div>

          {/* Main Card Body (2 Columns if featured) */}
          <div className={`offer-card-body-flex ${isFeatured ? 'has-3d-graphic' : ''}`}>
            {/* Left Content Side */}
            <div className="offer-left-info">
              <h3 className="offer-title-new">
                {offer?.title || `${type} Health Deal`}
              </h3>

              <div className="offer-slide-price">
                {isPercentageDeal ? (
                  <span className="offer-slide-discounted" style={{ color: '#facc15', textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>
                    {pctDisplay}
                  </span>
                ) : (
                  <>
                    <span className="offer-slide-discounted">${discPrice?.toFixed(2) || '0.00'}</span>
                    {origPrice > 0 && (
                      <span className="offer-slide-original">${origPrice.toFixed(2)}</span>
                    )}
                    {Number(saveAmt) > 0 && (
                      <span className="offer-slide-save">
                        Save ${saveAmt}
                      </span>
                    )}
                  </>
                )}
              </div>

              <p className="offer-subtitle-new">
                <motion.span
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: '4px' }}
                  animate={{ rotate: [0, -10, 10, -5, 0], scale: [1, 1.15, 1] }}
                  transition={{ repeat: Infinity, duration: 2.8, ease: 'easeInOut' }}
                >
                  {config.icon}
                </motion.span>
                <span>
                  {isPhleb 
                    ? 'Phlebotomy - Certified specialist doorstep blood draw service.' 
                    : `${offer?.category ? `${offer.category} - ` : ''}${offer?.description || config.subtitle}`}
                </span>
              </p>

              {/* Included Tests Chips Row on Hero Card */}
              {(() => {
                const rawIncludes = Array.isArray(offer?.includes) && offer.includes.length > 0
                  ? offer.includes
                  : [];
                
                let validTests = rawIncludes
                  .map((incItem, idx) => {
                    const matchedTest = resolveCatalogTest(incItem, catalogTests, {
                      category: offer?.category || 'Health Bundle',
                      price: discPrice,
                      discount_price: discPrice
                    });
                    if (!matchedTest) return null;
                    const fullTitle = matchedTest.title || matchedTest.name || `Test ${idx+1}`;
                    const cleanTitle = fullTitle.split(/[:(]/)[0].trim() || fullTitle;

                    return {
                      matchedTest,
                      fullTitle,
                      cleanTitle,
                      idx
                    };
                  })
                  .filter(Boolean);

                // If no catalog tests match or are included, do NOT render the included tests section
                if (validTests.length === 0) {
                  return null;
                }
                
                const firstTest = validTests[0];
                const extraCount = validTests.length - 1;

                return (
                  <div className="hero-included-tests-wrap">
                    <span className="hero-included-label">
                      Included Tests <span className="hero-inc-hint">(Click to view)</span>:
                    </span>
                    <div className="hero-inc-pills-row">
                      <button
                        key={firstTest.matchedTest.id || firstTest.matchedTest.code || firstTest.idx}
                        type="button"
                        className="hero-inc-pill-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedTestModal(firstTest.matchedTest);
                        }}
                        title={`View full details for ${firstTest.fullTitle}`}
                      >
                        <CheckCircle size={12} color="#34d399" style={{ flexShrink: 0 }} />
                        <span className="pill-title-text">{firstTest.cleanTitle}</span>
                        <span className="pill-arrow">›</span>
                      </button>

                      {extraCount > 0 && (
                        <button
                          type="button"
                          className="hero-inc-pill-btn hero-inc-more-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openTestModal(firstTest.matchedTest);
                          }}
                          title={`View all ${validTests.length} included tests`}
                        >
                          <span>+{extraCount} More</span>
                          <span className="pill-arrow">›</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Action Buttons Row */}
              <div className="offer-actions-btns-row" onClick={(e) => e.stopPropagation()}>
                <button 
                  type="button" 
                  className="btn-hero-details"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isPhleb) {
                      navigate('/book?category=phlebotomy');
                    } else {
                      const rawIncludes = Array.isArray(offer?.includes) && offer.includes.length > 0 ? offer.includes : [];
                      let matched = null;
                      for (const inc of rawIncludes) {
                        matched = resolveCatalogTest(inc, catalogTests);
                        if (matched) break;
                      }
                      if (!matched && offer?.title) {
                        matched = resolveCatalogTest(offer.title, catalogTests);
                      }
                      if (matched) {
                        setSelectedTestModal(matched);
                      } else {
                        navigate(`/offers?type=${type.toLowerCase()}`);
                      }
                    }
                  }}
                >
                  View Details <ArrowRight size={15} />
                </button>
                {isPhleb ? (
                  <button 
                    type="button" 
                    className="btn-hero-cart btn-hero-book"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate('/book?category=phlebotomy');
                    }}
                    title="Book Specialist Home Blood Draw"
                  >
                    <Calendar size={15} /> Book Now
                  </button>
                ) : (() => {
                  const offerId = offer?.id || offer?._id || `offer-${type}-${currentIdx}`;
                  const inCart = (cartItems || []).some(item => String(item.id || item._id) === String(offerId));
                  return (
                    <button 
                      type="button" 
                      className={`btn-hero-cart ${inCart ? 'added' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (inCart) {
                          removeFromCart(offerId);
                        } else {
                          triggerAddToCartAnimation(e, {
                            id: offerId,
                            title: offer?.title || `${type} Health Bundle`,
                            name: offer?.title || `${type} Health Bundle`,
                            price: discPrice,
                            originalPrice: origPrice,
                            image: offer?.image || '/assets/lavender_tube.png',
                            sample_type: offer?.category || 'Package Bundle'
                          });
                        }
                      }}
                      title={inCart ? "Click to remove from cart" : "Add to cart"}
                    >
                      <ShoppingCart size={15} /> {inCart ? '✓ Added' : '+ Add to Cart'}
                    </button>
                  );
                })()}
              </div>

              {/* Security & Dots Footer Row */}
              <div className="offer-footer-row">
                <div className="offer-security-footer-note">
                  <ShieldCheck size={14} className="ic-blue-shield" style={{ flexShrink: 0 }} />
                  <span>Secure checkout &bull; 100% privacy protected</span>
                </div>
                {offersList.length > 1 ? (
                  <div className="offer-dots" onClick={(e) => e.stopPropagation()}>
                    {offersList.map((_, i) => (
                      <button
                        key={i}
                        className={`offer-dot ${i === currentIdx ? 'active' : ''}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }}
                        title={`Slide ${i + 1} of ${offersList.length}`}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="offer-dots offer-dots-placeholder" aria-hidden="true" style={{ visibility: 'hidden', height: '18px', minWidth: '10px' }} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="home-page fade-in">
      {/* Dynamic Offers Slideshow Section */}
      {loading ? (
        <section className="dynamic-offers-container">
          <div className={`dynamic-offers-grid has-${skeletonSlots.length}-cards`}>
            {skeletonSlots.map((slot, idx) => (
              <div 
                key={slot.slotKey || slot.type || idx} 
                className={`offer-card-new ${slot.className} dynamic-offer-skeleton-card`}
                style={{ cursor: 'default' }}
              >
                {/* Gradient background with decorative shapes matching real card */}
                <div className="offer-card-bg" style={{ background: slot.gradient }}>
                  <div className="offer-card-bg-pattern" />
                  <div className="offer-deco offer-deco-1" />
                  <div className="offer-deco offer-deco-2" />
                  <div className="offer-deco offer-deco-3" />
                </div>

                <div className="offer-overlay">
                  {/* Top Bar: Badge + Timer */}
                  <div className="offer-overlay-top">
                    <div className="skeleton-shimmer-card" style={{ width: slot.badgeWidth, height: '26px', borderRadius: '100px' }} />
                    <div className="skeleton-shimmer-card" style={{ width: slot.timerWidth, height: '24px', borderRadius: '999px' }} />
                  </div>

                  {/* Body */}
                  <div className="offer-card-body-flex" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div className="offer-left-info" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {/* Title (2 lines matching .offer-title-new min-height: 2.8rem) */}
                      <div style={{ width: '100%', marginBottom: '0.25rem', minHeight: '2.8rem', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div className="skeleton-shimmer-card" style={{ width: '92%', height: '22px', borderRadius: '6px' }} />
                        <div className="skeleton-shimmer-card" style={{ width: '65%', height: '22px', borderRadius: '6px' }} />
                      </div>

                      {/* Price Row (Discounted price, original strike price, save badge) */}
                      <div className="offer-slide-price" style={{ display: 'flex', gap: '8px', alignItems: 'baseline', marginBottom: '0.3rem' }}>
                        <div className="skeleton-shimmer-card" style={{ width: '85px', height: '32px', borderRadius: '6px' }} />
                        <div className="skeleton-shimmer-card" style={{ width: '55px', height: '16px', borderRadius: '4px' }} />
                        <div className="skeleton-shimmer-card" style={{ width: '75px', height: '22px', borderRadius: '6px' }} />
                      </div>

                      {/* Subtitle / Description (2 lines matching .offer-subtitle-new min-height: 2.3rem) */}
                      <div style={{ width: '100%', marginBottom: '0.4rem', minHeight: '2.3rem', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <div className="skeleton-shimmer-card" style={{ width: '96%', height: '15px', borderRadius: '4px' }} />
                        <div className="skeleton-shimmer-card" style={{ width: '75%', height: '15px', borderRadius: '4px' }} />
                      </div>

                      {/* Included Tests Section (label + pills matching .hero-included-tests-wrap) */}
                      <div className="hero-included-tests-wrap" style={{ margin: '4px 0 6px 0', minHeight: '44px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <div className="skeleton-shimmer-card" style={{ width: '160px', height: '12px', borderRadius: '4px' }} />
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <div className="skeleton-shimmer-card" style={{ width: '145px', height: '26px', borderRadius: '999px' }} />
                          <div className="skeleton-shimmer-card" style={{ width: '80px', height: '26px', borderRadius: '999px' }} />
                        </div>
                      </div>

                      {/* Action Buttons Row */}
                      <div className="offer-actions-btns-row" style={{ display: 'flex', gap: '0.5rem', width: '100%', marginTop: 'auto', marginBottom: '0.25rem' }}>
                        <div className="skeleton-shimmer-card btn-skeleton-details" style={{ flex: 1, height: '38px', borderRadius: '10px' }} />
                        <div className="skeleton-shimmer-card btn-skeleton-cart" style={{ flex: 1.2, height: '38px', borderRadius: '10px' }} />
                      </div>

                      {/* Footer Row (Security note + slide dots) */}
                      <div className="offer-footer-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginTop: '0.45rem' }}>
                        <div className="skeleton-shimmer-card" style={{ width: '210px', height: '15px', borderRadius: '4px' }} />
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                          <div className="skeleton-shimmer-card" style={{ width: '18px', height: '7px', borderRadius: '999px' }} />
                          <div className="skeleton-shimmer-card" style={{ width: '7px', height: '7px', borderRadius: '50%' }} />
                          <div className="skeleton-shimmer-card" style={{ width: '7px', height: '7px', borderRadius: '50%' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : (showSeasonal || showMonthly || showWeekly || showPhleb) && (
        <section className="dynamic-offers-container">
          <div className={`dynamic-offers-grid ${gridClassName}`}>
            {/* Seasonal Slot (Renders First) */}
            {showSeasonal && renderSlideCard(
              seasonalOffers,
              seasonalIdx % seasonalOffers.length,
              setSeasonalIdx,
              'Seasonal',
              'seasonal'
            )}

            {/* Weekly Slot */}
            {showWeekly && renderSlideCard(
              weeklyOffers,
              weeklyIdx % weeklyOffers.length,
              setWeeklyIdx,
              'Weekly',
              'weekly'
            )}

            {/* Monthly Slot */}
            {showMonthly && renderSlideCard(
              monthlyOffers,
              monthlyIdx % monthlyOffers.length,
              setMonthlyIdx,
              'Monthly',
              'monthly'
            )}

            {/* Phlebotomy Slot (Only renders if offers exist) */}
            {showPhleb && renderSlideCard(
              phlebotomyOffers,
              phlebIdx % phlebotomyOffers.length,
              setPhlebIdx,
              'Phlebotomy',
              'phlebotomy-slot'
            )}
          </div>
        </section>
      )}

      {/* Section A: Hero + Quick Actions */}
      <section className="home-hero">
        <div className="hero-background"></div>
        <div className="home-hero-container">
          <div className="home-hero-main">
            <div className="hero-badge-modern">
              <span className="live-dot"></span> {heroData?.badge_text || 'Next-Gen Diagnostics'}
            </div>
            <h1 className="home-hero-title">
              High Quality &amp; Affordable Lab Testing <br />
              + Home Collections <span className="text-highlight">+ Research Support</span>
            </h1>
            <p className="home-hero-subtitle">
              {heroData?.subtitle && heroData.subtitle.toLowerCase().includes('research support')
                ? heroData.subtitle.replace(/^self-?pay/i, 'Validation pay')
                : 'Validation pay, Employer plans, Physicians, Facilities, and Research Support'}
            </p>
            
            <div className="home-hero-actions">
              <Link to="/tests" className="btn btn-primary btn-lg">
                Browse Tests <ArrowRight size={20} />
              </Link>
              <Link to="/book" className="btn btn-secondary btn-lg">
                Book Appointment
              </Link>
              <Link to="/offers" className="btn btn-outline btn-lg">
                View Offers
              </Link>
            </div>

            {/* Mobile App Download Buttons (Screenshot 2 Document Request) */}
            <div className="home-hero-app-downloads">
              <span className="home-hero-app-label">
                <Smartphone size={15} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                Easy Booking and Report Access:
              </span>
              <div className="home-hero-app-btns">
                <a 
                  href={appPromoConfig?.play_store_url || 'https://play.google.com/store/apps/details?id=com.musb.diagnostics&hl=en_IN'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-app-store-btn playstore"
                  title="Get MusB Diagnostics on Google Play"
                >
                  <svg viewBox="0 0 24 24" className="home-app-svg" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a2.03 2.03 0 0 1-.22-.964V2.778c0-.36.08-.696.22-.964z" fill="#00D2FF" />
                    <path d="M17.436 8.356L14.77 11.02 3.609 1.814a2.07 2.07 0 0 1 1.62-.27l12.207 6.812z" fill="#00E676" />
                    <path d="M17.436 15.644L5.229 22.456c-.53.296-1.13.364-1.62.27l11.16-10.206 2.667 3.124z" fill="#FF3D00" />
                    <path d="M21.282 10.505l-3.846-2.149-2.666 2.664 2.666 2.664 3.846-2.148c.718-.401.718-1.63 0-2.031z" fill="#FFC107" />
                  </svg>
                  <div className="home-app-btn-text">
                    <span className="home-app-sub">GET IT ON</span>
                    <span className="home-app-main">Google Play</span>
                  </div>
                </a>

                <a 
                  href={appPromoConfig?.app_store_url || 'https://apps.apple.com/in/app/musb-diagnostics/id6797962528'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-app-store-btn appstore"
                  title="Download MusB Diagnostics on App Store"
                >
                  <svg viewBox="0 0 170 170" className="home-app-svg apple" fill="currentColor">
                    <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.04-7.58-7.7-11.64-13.98-5.87-9.01-10.45-19.16-13.74-30.45-3.29-11.29-4.94-22.18-4.94-32.66 0-14.54 3.76-26.68 11.27-36.43 7.51-9.75 16.99-14.77 28.43-15.06 4.78 0 10.09 1.25 15.93 3.75 5.84 2.5 9.87 3.86 12.09 4.09 1.94-.34 6.13-1.84 12.57-4.51 6.44-2.67 12.09-3.77 16.94-3.32 12.4.92 22.36 5.58 29.89 13.98-10.74 6.5-16.02 15.48-15.84 26.94.19 8.94 3.65 16.43 10.38 22.47 6.73 6.04 14.88 9.54 24.45 10.51-2.45 7.42-5.46 14.78-9.03 22.08zM119.22 33.61c0-7.39 2.65-14.15 7.95-20.28 5.3-6.13 11.83-9.98 19.59-11.55.22 1.3.33 2.45.33 3.45 0 7.28-2.73 14.16-8.19 20.64-5.46 6.48-12.18 10.37-20.16 11.67-.32-1.3-.48-2.61-.48-3.93z"/>
                  </svg>
                  <div className="home-app-btn-text">
                    <span className="home-app-sub">Download on the</span>
                    <span className="home-app-main">App Store</span>
                  </div>
                </a>
              </div>
            </div>

            <div className="hero-search-widget glass">
              <form onSubmit={handleSearch}>
                <Search className="search-icon" size={24} />
                <input 
                  type="text" 
                  placeholder="Type to search for tests (e.g., Thyroid, Vitamin D)..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="btn btn-primary">Search</button>
              </form>
            </div>
          </div>


        </div>
      </section>


      {/* Section C: Live Offers Carousel */}
      <section className="section offers-section">
        <div className="offers-header">
          <div className="offers-header-left">
            <h2 className="section-title" style={{ marginBottom: '0.35rem', textAlign: 'left' }}>Live Health Offers</h2>
            <p className="section-subtitle" style={{ marginTop: '0', textAlign: 'left' }}>Exclusive weekly, monthly, and seasonal bundles.</p>
          </div>
          <Link to="/offers" className="offers-header-view-all">
            View All Offers <ArrowRight size={16}/>
          </Link>
        </div>

        <div className="offers-carousel-wrap">
          <button
            className="offers-carousel-arrow left"
            onClick={() => {
              const track = document.getElementById('offers-carousel-track');
              if (track) track.scrollBy({ left: -380, behavior: 'smooth' });
            }}
            aria-label="Scroll left"
          >
            ‹
          </button>

          <div className="offers-carousel-track" id="offers-carousel-track">
            {loading ? (
              [
                { type: 'seasonal', tagWidth: '95px' },
                { type: 'weekly', tagWidth: '85px' },
                { type: 'monthly', tagWidth: '95px' },
                { type: 'phlebotomy', tagWidth: '125px' }
              ].map((skel, i) => (
                <div key={i} className={`offer-tile deal-${skel.type} offer-tile-skeleton`}>
                  <div className="skeleton-shimmer-card" style={{ width: skel.tagWidth, height: '20px', borderRadius: '999px', marginBottom: '0.6rem' }} />
                  <div style={{ minHeight: '2.7rem', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '0.5rem' }}>
                    <div className="skeleton-shimmer-card" style={{ width: '90%', height: '18px', borderRadius: '4px' }} />
                    <div className="skeleton-shimmer-card" style={{ width: '60%', height: '18px', borderRadius: '4px' }} />
                  </div>
                  <div className="offer-price" style={{ marginBottom: '0.6rem', display: 'flex', gap: '6px', alignItems: 'baseline' }}>
                    <div className="skeleton-shimmer-card" style={{ width: '45px', height: '18px', borderRadius: '4px' }} />
                    <div className="skeleton-shimmer-card" style={{ width: '65px', height: '26px', borderRadius: '6px' }} />
                    <div className="skeleton-shimmer-card" style={{ width: '70px', height: '20px', borderRadius: '999px' }} />
                  </div>
                  <ul className="offer-includes" style={{ listStyle: 'none', padding: 0, margin: '0 0 0.6rem 0', minHeight: '3.2rem', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div className="skeleton-shimmer-card" style={{ width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0 }} />
                      <div className="skeleton-shimmer-card" style={{ width: '85%', height: '14px', borderRadius: '4px' }} />
                    </li>
                    <li style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div className="skeleton-shimmer-card" style={{ width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0 }} />
                      <div className="skeleton-shimmer-card" style={{ width: '70%', height: '14px', borderRadius: '4px' }} />
                    </li>
                  </ul>
                  <div className="offer-timer" style={{ background: 'transparent', border: 'none', padding: 0, marginBottom: '0.6rem', marginTop: 'auto' }}>
                    <div className="skeleton-shimmer-card" style={{ width: '100%', height: '36px', borderRadius: '8px' }} />
                  </div>
                  <div className="offer-actions" style={{ display: 'flex', gap: '8px' }}>
                    <div className="skeleton-shimmer-card" style={{ flex: 1, height: '36px', borderRadius: '24px' }} />
                    <div className="skeleton-shimmer-card" style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0 }} />
                  </div>
                </div>
              ))
            ) : offersData.length === 0 ? (
              <p className="full-width text-center" style={{ color: 'var(--text-muted)', padding: '2rem 0' }}>No active offers available right now.</p>
            ) : (
              offersData.map((offer, idx) => {
                const isPhleb = offer.offer_category === 'phlebotomy';
                const discStr = String(offer.discount_value || '');
                const titleStr = String(offer.title || '');
                const pctMatch = discStr.match(/(\d+)\s*%/) || titleStr.match(/(\d+)\s*%/);
                const hasExplicitMarketPrice = Boolean(offer.original_price || offer.market_price);
                const isPercentageDeal = Boolean(pctMatch) || offer.is_percentage === true || (!hasExplicitMarketPrice && isPhleb);
                const pctDisplay = pctMatch ? `${pctMatch[1]}% OFF` : (offer.discount_value || '20% OFF');

                const hasExplicitDiscount = offer.discounted_price !== undefined && offer.discounted_price !== null && String(offer.discounted_price).trim() !== '';
                const origVal = parseFloat(offer.original_price || offer.market_price || 0);
                let discVal = hasExplicitDiscount ? parseFloat(offer.discounted_price) : null;
                if (discVal === null && origVal > 0 && pctMatch) {
                  discVal = origVal * (1 - parseFloat(pctMatch[1]) / 100);
                }
                const isFree = (hasExplicitDiscount && discVal === 0) || (discVal === 0) || (titleStr.toLowerCase().includes('free') && (discVal === 0 || discVal === null));
                const effectivePrice = isFree ? 0 : (discVal !== null && !isNaN(discVal) ? discVal : origVal);
                const showStrike = origVal > 0 && origVal > effectivePrice;
                const saveVal = showStrike ? Math.round(origVal - effectivePrice) : 0;

                return (
                  <div key={`home-offer-${offer.offer_category || 'deal'}-${offer.id || offer._id || idx}`} className={`offer-tile deal-${(isPhleb ? 'phlebotomy' : (offer.offer_type || 'weekly')).toLowerCase()}`}>
                    <div className="offer-tag">{isPhleb ? 'Mobile Phlebotomy' : `${offer.offer_type} Deal`}</div>
                    <h3>{offer.title}</h3>
                    
                    <div className="offer-price">
                      {isPercentageDeal ? (
                        <span className="price-new" style={{ color: '#facc15', fontSize: '1.75rem', fontWeight: 800 }}>
                          {pctDisplay}
                        </span>
                      ) : isFree ? (
                        <>
                          {showStrike && (
                            <span className="price-strike">${origVal % 1 === 0 ? origVal.toFixed(0) : origVal.toFixed(2)}</span>
                          )}
                          <span className="price-new" style={{ color: '#34d399', fontSize: '1.75rem', fontWeight: 800 }}>
                            FREE
                          </span>
                          <span className="save-badge">
                            {saveVal > 0 ? `SAVE $${saveVal}` : '100% OFF'}
                          </span>
                        </>
                      ) : (
                        <>
                          {showStrike && (
                            <span className="price-strike">${origVal % 1 === 0 ? origVal.toFixed(0) : origVal.toFixed(2)}</span>
                          )}
                          <span className="price-new">
                            ${effectivePrice % 1 === 0 ? effectivePrice.toFixed(0) : effectivePrice.toFixed(2)}
                          </span>
                          {saveVal > 0 && (
                            <span className="save-badge">
                              SAVE ${saveVal}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    <ul className="offer-includes">
                      {Array.isArray(offer.includes) && offer.includes.length > 0 ? (
                        offer.includes.slice(0, 2).map((item, i) => (
                          <li key={i}>
                            <CheckCircle size={16} className="check-icon" style={{ flexShrink: 0 }} />
                            <span>{typeof item === 'string' ? item : (item.title || item.name)}</span>
                          </li>
                        ))
                      ) : isPhleb ? (
                        <>
                          <li><CheckCircle size={16} className="check-icon" style={{ flexShrink: 0 }} /> <span>Doorstep Clinical Blood Collection</span></li>
                          <li><CheckCircle size={16} className="check-icon" style={{ flexShrink: 0 }} /> <span>Certified Specialist &amp; Cold-Chain Logistics</span></li>
                        </>
                      ) : (() => {
                        let incList = [];
                        if (typeof offer.includes === 'string' && offer.includes.trim()) {
                          incList = offer.includes.split(',').map(s => s.trim()).filter(Boolean);
                        } else if (offer.description && offer.description.trim()) {
                          incList = [offer.description];
                        } else if (offer.category) {
                          incList = [`${offer.category} Diagnostic Test Panel`];
                        } else {
                          incList = [`${offer.title || 'Comprehensive Health'} Bundle`];
                        }
                        return incList.slice(0, 2).map((item, i) => (
                          <li key={i}>
                            <CheckCircle size={16} className="check-icon" style={{ flexShrink: 0 }} />
                            <span>{item}</span>
                          </li>
                        ));
                      })()}
                    </ul>

                    <div className="offer-timer">
                      <CountdownTimer 
                        startDate={offer.start_date}
                        endDate={offer.end_date}
                        timeLeft={offer.time_left}
                        isUnlimited={offer.is_unlimited}
                      />
                    </div>

                    <div className="offer-actions">
                      <button 
                        type="button"
                        className="btn btn-white btn-sm"
                        onClick={(e) => {
                          e.preventDefault();
                          if (isPhleb) {
                            navigate('/book?category=phlebotomy');
                          } else {
                            const offerId = offer?.id || offer?._id || `offer-${idx}`;
                            addToCart({
                              id: offerId,
                              title: offer?.title || 'Health Bundle Deal',
                              name: offer?.title || 'Health Bundle Deal',
                              price: effectivePrice,
                              originalPrice: origVal || effectivePrice,
                              image: offer?.image || '/assets/lavender_tube.png',
                              sample_type: offer?.category || 'Package Bundle'
                            });
                            navigate('/cart');
                          }
                        }}
                      >
                        Book Deal
                      </button>
                      <button 
                        type="button"
                        className="btn-icon" 
                        title="Share Offer"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShareModalData({
                            isOpen: true,
                            test: {
                              id: offer.id || offer._id,
                              title: offer.title,
                              name: offer.title,
                              category: offer.category || 'Offers'
                            }
                          });
                        }}
                      >
                        <Share2 size={18}/>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <button
            className="offers-carousel-arrow right"
            onClick={() => {
              const track = document.getElementById('offers-carousel-track');
              if (track) track.scrollBy({ left: 380, behavior: 'smooth' });
            }}
            aria-label="Scroll right"
          >
            ›
          </button>
        </div>
      </section>

      {services.length > 0 && (
        <section className="section bg-light-alt how-it-works">
          <h2 className="section-title">Seamless Testing Experience</h2>
          <div className="steps-container">
            {services.map((service, idx) => (
              <React.Fragment key={`home-service-${service.id || service.title || idx}`}>
                <div 
                  className={`step-card-flip ${flippedIdx === idx ? 'is-flipped' : ''}`}
                  onClick={() => setFlippedIdx(flippedIdx === idx ? null : idx)}
                >
                  <div className="step-card-inner">
                    {/* Front Side */}
                    <div className="step-card-front">
                      <div className="step-card-title-wrap">
                        <motion.div 
                          className="step-icon"
                          animate={{ rotate: [0, -8, 8, 0], y: [0, -3, 0] }}
                          transition={{ repeat: Infinity, duration: 2.8 + (idx % 4) * 0.3, ease: 'easeInOut' }}
                          whileHover={{ scale: 1.18, rotate: 10 }}
                        >
                          {getIcon(service.icon_name)}
                        </motion.div>
                        <h3>{service.title}</h3>
                      </div>
                      <div className="flip-hint">Click to learn more</div>
                    </div>
                    
                    {/* Back Side */}
                    <div className="step-card-back">
                      <h3>{service.title}</h3>
                      <p>{service.description}</p>
                      <Link to={service.link || '/tests'} className="btn-link-white" onClick={(e) => e.stopPropagation()}>
                        View Service <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </section>
      )}

      {/* Section E: Trust & Compliance */}
      <section className="trust-section">
        <div className="trust-content">
          <div className="trust-text">
            <h2>Uncompromising Quality & Compliance</h2>
            <p>Your health data is secure, and our results are research-grade accurate.</p>
            <ul className="trust-list">
              <li>
                <motion.span animate={{ scale: [1, 1.15, 1] }} transition={{ repeat: Infinity, duration: 2.5, ease: 'easeInOut' }} style={{ display: 'inline-flex', flexShrink: 0 }}>
                  <CheckCircle className="text-primary"/>
                </motion.span>
                <strong>HIPAA Compliant</strong> <span>- Privacy &amp; Data Security</span>
              </li>
              <li>
                <motion.span animate={{ scale: [1, 1.15, 1] }} transition={{ repeat: Infinity, duration: 2.8, ease: 'easeInOut' }} style={{ display: 'inline-flex', flexShrink: 0 }}>
                  <CheckCircle className="text-primary"/>
                </motion.span>
                <strong>Fast Turnaround</strong> <span>- 24-48h Results</span>
              </li>
              <li>
                <motion.span animate={{ scale: [1, 1.15, 1] }} transition={{ repeat: Infinity, duration: 3.1, ease: 'easeInOut' }} style={{ display: 'inline-flex', flexShrink: 0 }}>
                  <CheckCircle className="text-primary"/>
                </motion.span>
                <strong>Precision Assured</strong> <span>- Medical &amp; Research-Grade Accuracy</span>
              </li>
            </ul>
          </div>
          <div className="trust-badges">
            <motion.div 
              className="badge-item badge-item-accredited"
              whileHover={{ y: -6, scale: 1.04 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <div className="badge-logo-pill">
                <img 
                  src="/images/clia-logo.png" 
                  alt="CLIA Certified - Clinical Laboratory Improvement Amendments" 
                  className="badge-logo-img" 
                />
              </div>
              <span className="badge-name">CLIA Certified</span>
            </motion.div>
            <motion.div 
              className="badge-item badge-item-accredited"
              whileHover={{ y: -6, scale: 1.04 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <div className="badge-logo-pill">
                <img 
                  src="/images/cola-logo.png" 
                  alt="COLA Accredited - Commission on Laboratory Accreditation" 
                  className="badge-logo-img" 
                />
              </div>
              <span className="badge-name">COLA Accredited</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section F: Popular Diagnostic Tests */}
      <section className="section panels-section bg-light-alt" style={{ padding: '3.5rem 2rem', textAlign: 'center' }}>
        <div className="section-header text-center" style={{ marginBottom: '1.75rem' }}>
          <h2 className="section-title">Popular Diagnostic Tests</h2>
        </div>
        <div className="center-btn-wrap">
          <Link to="/tests" className="btn btn-outline btn-lg">Explore All Diagnostic Tests <ArrowRight size={20} /></Link>
        </div>
      </section>

      {/* Section G: Social proof + community */}
      <section className="section community-section">
        <div className="community-blob blob-1"></div>
        <div className="community-blob blob-2"></div>

        <motion.h2 
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Trusted by the Community
        </motion.h2>

        <div className="community-grid">
          <motion.div 
            className="testimonials"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Quote className="quote-icon" size={40} />
            {(() => {
              const activeTestimonial = (testimonials && testimonials.length > 0)
                ? testimonials[activeReviewIdx % testimonials.length]
                : {
                    content: "MusB Diagnostics made mobile phlebotomy and diagnostic testing so seamless. Fast turnaround, verified accuracy, and transparent pricing!",
                    author_name: "Verified Patient",
                    rating: 5
                  };
              const rawName = activeTestimonial.author_name || activeTestimonial.author || 'Verified Patient';
              const isGoogle = activeTestimonial.source === 'google' || String(rawName).toLowerCase().includes('google');
              const isVerified = String(rawName).toLowerCase().includes('verified') || isGoogle;
              const cleanInitials = rawName.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase() || 'VP';
              return (
                <>
                  <motion.div
                    key={activeReviewIdx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: 'easeOut' }}
                    style={{ minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
                  >
                    <p className="testimonial-text">"{activeTestimonial.content || activeTestimonial.text}"</p>
                    <div className="testimonial-author">
                      <motion.div 
                        className="author-avatar"
                        animate={{ scale: [1, 1.06, 1] }}
                        transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                        style={{ 
                          width: '46px',
                          height: '46px',
                          color: isGoogle ? '#4285F4' : '#ffffff', 
                          fontWeight: 800, 
                          fontSize: '0.88rem', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          borderRadius: '50%',
                          border: isGoogle ? '2px solid rgba(56, 189, 248, 0.4)' : 'none',
                          boxShadow: '0 3px 10px rgba(0, 0, 0, 0.15)',
                          flexShrink: 0,
                          overflow: 'hidden'
                        }}
                      >
                        <TestimonialAvatar 
                          photoUrl={activeTestimonial.author_photo_url} 
                          name={rawName} 
                          isGoogle={isGoogle} 
                          isVerified={isVerified} 
                          cleanInitials={cleanInitials} 
                        />
                      </motion.div>
                      <div className="testimonial-info">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <strong>{rawName}</strong>
                          {isGoogle && (
                            <span style={{ fontSize: '0.72rem', background: '#e8f0fe', color: '#1a73e8', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <svg width="11" height="11" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
                              Google Review
                            </span>
                          )}
                        </div>
                        <div className="stars" style={{ color: '#f59e0b', display: 'flex', gap: '2px', marginTop: '2px' }}>
                          {[...Array(activeTestimonial.rating || 5)].map((_, i) => <Star key={i} size={14} fill="currentColor"/>)}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  {testimonials.length > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1.25rem' }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {testimonials.slice(0, 10).map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setActiveReviewIdx(idx)}
                            style={{
                              width: idx === (activeReviewIdx % testimonials.length) ? '20px' : '7px',
                              height: '7px',
                              borderRadius: '999px',
                              background: idx === (activeReviewIdx % testimonials.length) ? '#2563eb' : '#cbd5e1',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              transition: 'all 0.3s ease'
                            }}
                            aria-label={`View review ${idx + 1}`}
                          />
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setActiveReviewIdx(prev => (prev - 1 + testimonials.length) % testimonials.length)}
                          aria-label="Previous review"
                          style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '50%',
                            border: '1px solid #e2e8f0',
                            background: '#ffffff',
                            color: '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            padding: 0
                          }}
                        >
                          <ChevronLeft size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveReviewIdx(prev => (prev + 1) % testimonials.length)}
                          aria-label="Next review"
                          style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '50%',
                            border: '1px solid #e2e8f0',
                            background: '#ffffff',
                            color: '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            padding: 0
                          }}
                        >
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </motion.div>

          <motion.div 
            className="community-heroes"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <motion.div 
              className="heroes-icon"
              animate={{ rotate: [0, -8, 8, 0], y: [0, -3, 0] }}
              transition={{ repeat: Infinity, duration: 3.2, ease: 'easeInOut' }}
              whileHover={{ scale: 1.18, rotate: 10 }}
            >
              <Users size={26} />
            </motion.div>
            <h3>Community Heroes</h3>
            <p>
              Supporting non-profits and study participants across the nation. Over <strong>{communityStats?.tests_delivered_display || '1,200+'}</strong> subsidized and diagnostic tests provided for underserved communities and clinical research trials.
            </p>
            <Link to="/contact" className="btn btn-primary">Join Our Mission</Link>
          </motion.div>
        </div>
      </section>

      {/* Section H: Newsletter + Blog preview */}
      <section className="newsletter-section">
        <div className="newsletter-card">
          <h2 style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.55rem', fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.35rem' }}>
            <motion.span
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              animate={{ rotate: [0, -10, 10, -5, 0], y: [0, -3, 0] }}
              transition={{ repeat: Infinity, duration: 3.2, ease: 'easeInOut' }}
            >
              <Mail size={24} style={{ color: '#d32029', flexShrink: 0 }} />
            </motion.span>
            <span>Monthly Health Offers &amp; Tips</span>
          </h2>
          <p>Subscribe to stay updated with our newest test panels, seasonal discounts, and wellness advice.</p>
          <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
            <div className="newsletter-input-wrapper">
              <input 
                type="email" 
                placeholder="Enter your email address" 
                required 
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={newsletterStatus.loading}
              />
              <button type="submit" className="btn newsletter-btn" disabled={newsletterStatus.loading}>
                {newsletterStatus.loading ? <Loader2 className="animate-spin" size={18}/> : 'Subscribe Now'}
              </button>
            </div>
            {newsletterStatus.message && (
              <div className={`newsletter-msg ${newsletterStatus.error ? 'error' : 'success'}`}>
                {newsletterStatus.message}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* ── Test Details Modal (Full Detailed Layout) ── */}
      {selectedTestModal && createPortal(
        <div 
          className="test-modal-backdrop" 
          onClick={closeTestModal}
          onWheel={(e) => {
            const container = e.currentTarget.querySelector('.test-modal-card-container');
            if (container && e.target === e.currentTarget) {
              container.scrollTop += e.deltaY;
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 200000000,
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            margin: 0,
            overflow: 'hidden'
          }}
        >
          <div 
            className="test-modal-card-container" 
            onClick={e => e.stopPropagation()}
            style={{
              width: '95vw',
              maxWidth: '1200px',
              maxHeight: '90vh',
              background: '#ffffff',
              borderRadius: '1.25rem',
              overflowY: 'auto',
              scrollBehavior: 'smooth',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              position: 'relative'
            }}
          >
            <TestDetailContent 
              test={selectedTestModal} 
              isModal={true} 
              onClose={closeTestModal} 
            />
          </div>
        </div>,
        document.body
      )}

      <ShareModal
        isOpen={shareModalData.isOpen}
        onClose={() => setShareModalData({ isOpen: false, test: null })}
        title="Share Diagnostic Test"
        testName={shareModalData.test?.title || shareModalData.test?.name || 'Diagnostic Test'}
        testId={shareModalData.test?.id || shareModalData.test?._id || shareModalData.test?.code}
        testSlug={shareModalData.test?.slug || (shareModalData.test?.title ? shareModalData.test.title.toLowerCase().replace(/[^a-z0-9]+/g, '-') : '')}
        category={shareModalData.test?.category_names ? (Array.isArray(shareModalData.test.category_names) ? shareModalData.test.category_names[0] : shareModalData.test.category_names) : (shareModalData.test?.category_name || shareModalData.test?.category)}
      />
    </div>
  );
};

export default Home;